import { HttpClient, HttpResponse } from '@angular/common/http';
import { StorageProvider } from '../storage/storage';
import { NetworkProvider } from '../network/network';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { OfflineSyncProvider } from '../offline-sync/offline-sync';
import { AlertController } from '@ionic/angular';
import { HTTP_STATUS_CODE } from '../../utils/http_status_code';
import { OfflinePostCrAndRelevesAndPrimesRequest } from '../activites/post-cr.service';
import { InitEntry } from './get-init-response.model';

export enum TypeDataRequest {
  AUTRES,
  PRIMES,
  RELEVES,
  CRS_UNPLANNED,
  CRS_WITH_RELEVES_AND_PRIMES,
}

export const GET_INIT = {
  DELAY_BETWEEN_RETRIES: 4000,
  MAX_RETRIES: 60,
};

/**
 * Requests Provider
 *
 * Ce service permet d'effectuer toutes les rêquetes http (get,post,put,delete) avec une posisibilité de gérer un mode hors-ligne
 */
@Injectable({ providedIn: 'root' })
export class RequestsProvider {
  /**
   * Compteur pour le retry des requetes offline
   */
  retryLoopRequest = 0;

  /**
   * Logger
   */
  private logger: LoggerWrapper;

  /**
   * Constructeur
   *
   * Crée une instance de RequestsProvider.
   *
   * @param loggerFactory
   * @param {HttpClient} http
   * @param {StorageProvider} storageProvider
   * @param {OfflineSyncProvider} offlineSyncProvider
   * @param alertCtrl
   * @param {NetworkProvider} networkProvider
   */
  constructor(
    private loggerFactory: LoggerFactory,
    private http: HttpClient,
    private storageProvider: StorageProvider,
    private offlineSyncProvider: OfflineSyncProvider,
    public alertCtrl: AlertController,
    private networkProvider: NetworkProvider
  ) {
    this.logger = this.loggerFactory.getInstance(this, true);
  }

  /**
   * Méthode GET classique
   *
   * @param {string} url url de la requete
   * @param {object} params parametres de la requete
   * @param {object} headers headers de la requete
   */
  get(url, params = null, headers = null): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (await this.networkProvider.isReallyConnected()) {
        this.http.get(url).subscribe(
          (data) => {
            resolve(data);
          },
          (err) => {
            reject(err);
          }
        );
      } else {
        reject(null);
      }
    });
  }

  /**
   * Méthod transformant un post en promise
   *
   * @param {string} url url de la requete
   */
  async postToPromise(url): Promise<any> {
    return this.http.post<HttpResponse<any>>(url, {}, { observe: 'response' }).toPromise();
  }

  /**
   * Méthode GET qui recommence son appel jusqu'a obternir un 200
   */
  async getWithPulling(url: string): Promise<InitEntry[]> {
    const maxRetries = GET_INIT.MAX_RETRIES;
    const retryDelay = GET_INIT.DELAY_BETWEEN_RETRIES;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        const response = await this.postToPromise(url);
        if (response.status === HTTP_STATUS_CODE.OK) {
          return response.body;
        }
      } catch (error) {
        throw new Error(`Failed to retrieve data err: ${error}`);
      }
      retryCount++;
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
    }
    throw new Error(`Failed to retrieve data after ${maxRetries} attempts.`);
  }

  /**
   * Méthode GET hors-ligne
   *
   * Si on est connecté, on execute la requete et on stocke le resultat
   *
   * Si on est déconnecté, on récupère le resultat à partir du stockage local
   *
   * @param {string} url url de la requete
   * @param {object} params parametres de la requete
   * @param {object} headers headers de la requete
   * @param each_doc
   */
  getOffline(url, params = null, headers = null, each_doc = false) {
    return new Promise(async (resolve) => {
      let storage_name = this.storageProvider.getStorageName(url, params);
      if (await this.networkProvider.isReallyConnected()) {
        this.http.get(url, { headers: headers }).subscribe(
          (data) => {
            resolve(data);
          },
          (err) => {
            console.log(err);
            this.storageProvider.set(storage_name, []);
            resolve([]);
          }
        );
      } else {
        let data = this.storageProvider.get(storage_name, each_doc);
        resolve(data);
      }
    });
  }

  /**
   * Méthode GET hors-ligne
   *
   * Si la donnée est en cache alors on utilise le cache, sinon on execute la requete et on stocke le resultat
   *
   * @param {string} url url de la requete
   * @param key
   * @param {object} params parametres de la requete
   */
  async getOfflineFirst<T = any>(url: string, key: string | null = null, params: any | null = null): Promise<T> {
    if (key == null) key = this.storageProvider.getStorageName(url, params);

    if (await this.storageProvider.exist(key)) {
      console.log('Get from cache: ', key);
      return await this.storageProvider.get(key, false);
    } else {
      console.log('Get without cache: ', key);
      return await firstValueFrom(this.http.get<T>(url));
    }
  }

  async getOfflineFirstWithLog<T = any>(url: string, key: string | null = null, params: any | null = null): Promise<T> {
    if (key == null) key = this.storageProvider.getStorageName(url, params);

    if (await this.storageProvider.exist(key)) {
      this.logger.info('Get from cache: ', key);
      return await this.storageProvider.get(key, false);
    } else {
      this.logger.info('Get without cache: ', key);
      return await firstValueFrom(this.http.get<T>(url));
    }
  }

  /**
   * Méthode GET hors-ligne
   *
   * Si on est connecté, on execute la requete et on stocke le resultat
   *
   * Si on est déconnecté, on récupère le resultat à partir du stockage local
   *
   * @param {string} url url de la requete
   * @param {object} params parametres de la requete
   * @param {object} headers headers de la requete
   * @param each_doc
   */
  getOfflineFirstOld(url) {
    console.log('getOfflineFirst: ' + url);
    return new Promise(async (resolve, reject) => {
      let storage_name = this.storageProvider.getStorageName(url, null);
      console.log('getOfflineFirst storage_name: ' + storage_name);
      if (await this.storageProvider.exist(storage_name)) {
        console.log('exist: ' + storage_name);
        let data = await this.storageProvider.get(storage_name, false);
        console.log('getOfflineFirst data: ' + url, data);
        resolve(data);
      } else {
        if (await this.networkProvider.isReallyConnected()) {
          this.http.get(url, { headers: null }).subscribe(
            (data) => {
              resolve(data);
            },
            (err) => {
              reject(`Impossible de se connecter à ${JSON.stringify(err)}`);
            }
          );
        } else {
          reject(`${url} non en cache et offline`);
        }
      }
    });
  }

  /**
   * Méthode PUT classique
   *
   * @param {string} url url de la requete
   * @param {object} data parametres de la requete
   * @param {object} headers headers de la requete
   */
  put(url, data, headers: any = { 'Content-Type': 'application/json' }) {
    return new Promise(async (resolve, reject) => {
      if (await this.networkProvider.isReallyConnected()) {
        this.http.put(url, data, { headers: headers }).subscribe(
          (data) => {
            resolve(data);
          },
          (err) => {
            reject(err);
          }
        );
      } else {
        reject(null);
      }
    });
  }

  /**
   * Méthode PUT hors-ligne
   *
   * Si on est connecté, on execute la requete
   *
   * Si on est déconnecté, on en registre la requete dans le offlineSyncProvider pour quelle soit éxécuté quand il y aura de la connexion
   *
   * @param {string} url url de la requete
   * @param {object} data parametres de la requete
   * @param {object} headers headers de la requete
   */
  putOffline(url, data, headers = { 'Content-Type': 'application/json' }) {
    return new Promise(async (resolve, reject) => {
      if (await this.networkProvider.isReallyConnected()) {
        this.http.put(url, data, { headers: headers }).subscribe(
          (data) => {
            resolve(data);
          },
          (err) => {
            reject(err);
          }
        );
      } else {
        console.log('putOffline offline');
        data.offline = 'waiting';
        this.storageProvider
          .set(data.id, data)
          .then(() => {
            return this.offlineSyncProvider.add('PUT', url, data, headers);
          })
          .then(() => {
            resolve(data);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }

  /**
   * Méthode POST classique
   *
   * @param {string} url url de la requete
   * @param {object} data parametres de la requete
   * @param {object} headers headers de la requete
   */
  post(url, data, headers = { 'Content-Type': 'application/json' }): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (await this.networkProvider.isReallyConnected()) {
        // this.logger.log("data",data);
        this.http.post(url, data, { headers: headers }).subscribe(
          (data) => {
            resolve(data);
          },
          (err) => {
            reject(err);
          }
        );
      } else {
        reject(null);
      }
    });
  }

  /**
   * Méthode POST hors-ligne
   *
   * Si on est connecté, on execute la requete
   *
   * Si on est déconnecté, on en registre la requete dans le offlineSyncProvider pour quelle soit éxécuté quand il y aura de la connexion
   *
   * @param {string} url url de la requete
   * @param {object} data parametres de la requete
   * @param {object} headers headers de la requete
   * @param {object} get_url url de la requete get (pour la mise à jour du stockage)
   * @param {object} get_params parametres de la requete get (pour la mise à jour du stockage)
   * @param type
   * @param force_offline
   */
  postOffline(
    url,
    data,
    headers = { 'Content-Type': 'application/json' },
    get_url,
    get_params = null,
    type = null,
    force_offline = false
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (await this.modeOnline(force_offline)) {
        this.httpPost(url, data, headers, resolve, reject);
      } else {
        data.offline = 'waiting';
        data.type = type;

        const alreadyScheduledForOfflinePost = await this.checkIfAlreadyScheduledForOfflinePost(data);

        if (alreadyScheduledForOfflinePost) {
          resolve('doublon');
          return;
        }

        data.id = new Date().getTime().toString();

        this.offlineSyncProvider.add('POST', url, data, headers);
        resolve(data);
      }
    });
  }

  public async checkIfAlreadyScheduledForOfflinePost(data: OfflinePostCrAndRelevesAndPrimesRequest): Promise<boolean> {
    let offline_list = await this.storageProvider.get('offline', false);

    // CONTROLE ANTI DOUBLON
    let finded = -1;
    if (offline_list && offline_list.length > 0) {
      finded = offline_list.findIndex((item) => {
        delete item.data.id;
        return JSON.stringify(item.data) == JSON.stringify(data);
      });
    }
    return Promise.resolve(finded != -1);
  }

  public httpPost(
    url,
    data,
    headers: { 'Content-Type': string },
    resolve: (value?: any) => void,
    reject: (reason?: any) => void
  ) {
    this.http.post(url, data, { headers: headers }).subscribe(
      (data) => {
        this.networkProvider.addHistory('Envoie de la requete offline OK', url);
        resolve(data);
      },
      (err) => {
        reject(err);
      }
    );
  }

  public async modeOnline(force_offline: boolean) {
    return !force_offline && (await this.networkProvider.isReallyConnected());
  }

  /**
   * Méthode PATCH classique
   *
   * @param {string} url url de la requete
   * @param {object} data parametres de la requete
   * @param {object} headers headers de la requete
   */
  patch(url, data, headers = { 'Content-Type': 'application/json' }) {
    return new Promise(async (resolve, reject) => {
      if (await this.networkProvider.isReallyConnected()) {
        this.http.patch(url, data, { headers: headers }).subscribe(
          (data) => {
            resolve(data);
          },
          (err) => {
            reject(err);
          }
        );
      } else {
        reject(null);
      }
    });
  }

  /**
   * Méthode PATCH hors-ligne
   *
   * Si on est connecté, on execute la requete
   *
   * Si on est déconnecté, on en registre la requete dans le offlineSyncProvider pour quelle soit éxécuté quand il y aura de la connexion
   *
   * @param {string} url url de la requete
   * @param {object} data parametres de la requete
   * @param {object} headers headers de la requete
   * @param {object} get_url url de la requete get (pour la mise à jour du stockage)
   * @param {object} get_params parametres de la requete get (pour la mise à jour du stockage)
   */
  patchOffline(url, data, headers = { 'Content-Type': 'application/json' }, get_url, get_params = null) {
    return new Promise(async (resolve, reject) => {
      if (await this.networkProvider.isReallyConnected()) {
        this.http.patch(url, data, { headers: headers }).subscribe(
          (data) => {
            resolve(data);
          },
          (err) => {
            reject(err);
          }
        );
      } else {
        let storage_name = this.storageProvider.getStorageName(get_url, get_params);
        this.storageProvider
          .get(storage_name)
          .then((storage_list) => {
            if (storage_list == null) storage_list = [];

            data.id = new Date().getTime().toString();
            data.offline = 'waiting';
            // this.logger.log("before push",storage_list);
            // this.logger.log("push to storage_list",data);
            storage_list.push(data);
            // this.logger.log("storage_list",storage_list);
            return this.storageProvider.set(storage_name, storage_list);
          })
          .then(() => {
            return this.storageProvider.set(data['id'], data);
          })
          .then(() => {
            this.offlineSyncProvider.add('POST', url, data, headers);
            resolve(data);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  }

  /**
   * Méthode DELETE classique
   *
   * @param {string} url url de la requete
   * @param {object} options options de la requete
   */
  delete(url, options = {}) {
    return new Promise((resolve, reject) => {
      this.http.delete(url, options).subscribe(
        (data) => {
          resolve(data);
        },
        (err) => {
          reject(err);
        }
      );
    });
  }

  /**
   * Méthode DELETE hors-ligne
   *
   * Si on est connecté, on execute la requete et on supprime les données dans le storage
   *
   * Si on est déconnecté, on en registre la requete dans le offlineSyncProvider pour quelle soit éxécuté quand il y aura de la connexion
   *
   * @param {string} url url de la requete
   * @param {object} id id du document
   * @param {object} options options pour la requete
   */
  deleteOffline(url, id, options = {}) {
    return new Promise(async (resolve, reject) => {
      if (await this.networkProvider.isReallyConnected()) {
        this.http.delete(url, options).subscribe(
          (data) => {
            this.storageProvider.remove(id);
            resolve(data);
          },
          (err) => {
            reject(err);
          }
        );
      } else {
        this.storageProvider
          .remove(id)
          .then((data) => {
            this.offlineSyncProvider.add('DELETE', url, { id: id }, {});
            resolve(data);
          })
          .catch((err) => {
            if (err == undefined) {
            }
            reject(null);
          });
      }
    });
  }

  /**
   * Call storageProvider getStorageName (injection)
   */
  getStorageName(url: string) {
    return this.storageProvider.getStorageName(url);
  }

  /**
   * Call storageProvider get (injection)
   */
  storageProviderGet(storage_name) {
    return this.storageProvider.get(storage_name, false);
  }

  /**
   * Call storageProvider set (injection)
   */
  storageProviderSet(storage_name, data, each_doc = true) {
    return this.storageProvider.set(storage_name, data, each_doc);
  }

  async removeExistingGetInitData() {
    await this.storageProvider.removeExistingGetInitData();
  }
}
